import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
  z-index: ${props => (props.$open ? 40 : 1)};
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 42px 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  height: 40px;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: #005aab;
    box-shadow: 0 0 0 2px rgba(0, 90, 171, 0.2);
  }
`;

const Dropdown = styled.div`
  position: fixed;
  max-height: 420px;
  overflow-y: auto;
  overflow-x: hidden;
  background: white;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  z-index: 100000;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.18);
  box-sizing: border-box;

  @media (max-width: 700px) {
    max-height: 300px;
  }
`;

const LupaButton = styled.button`
  position: absolute;
  top: 50%;
  right: 6px;
  transform: translateY(-50%);
  background: #005aab;
  border: none;
  color: #fff;
  width: 30px;
  height: 30px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;

  &:hover { opacity: 0.9; }
`;

const gridCols = (campos) => {
  if (!campos || campos.length <= 1) return '1fr';
  const parts = campos.map((c, i) => {
    const k = (c.key || '').toLowerCase();
    if (k === 'codigo' || k === 'code') return 'minmax(110px, 150px)';
    if (i === 0 && campos.length === 2) return 'minmax(110px, 150px)';
    return 'minmax(280px, 1fr)';
  });
  return parts.join(' ');
};

const DropdownItem = styled.div`
  padding: 10px 14px;
  cursor: pointer;
  display: grid;
  grid-template-columns: ${props => props.$grid};
  gap: 16px;
  align-items: start;
  border-bottom: 1px solid #f1f5f9;

  &:hover {
    background-color: #f0f7ff;
  }
  &.selected {
    background-color: #e6f3ff;
  }
`;

const ColumnHeader = styled.div`
  padding: 10px 14px;
  background-color: #f8fafc;
  font-weight: 700;
  font-size: 0.85rem;
  color: #334155;
  display: grid;
  grid-template-columns: ${props => props.$grid};
  gap: 16px;
  border-bottom: 2px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const Column = styled.div`
  min-width: 0;
  padding: 0 2px;
  font-size: 0.92rem;
  color: #0f172a;
  white-space: ${props => (props.$wrap ? 'normal' : 'nowrap')};
  overflow: ${props => (props.$wrap ? 'visible' : 'hidden')};
  text-overflow: ${props => (props.$wrap ? 'clip' : 'ellipsis')};
  line-height: 1.35;
  word-break: ${props => (props.$wrap ? 'break-word' : 'normal')};
`;

const DEFAULT_MIN_WIDTH = 720;

const BuscaDinamica = ({
  options = [],
  onSelect,
  defaultValue,
  campos = [{ key: 'descricao', label: 'Descrição' }],
  placeholder = 'Digite para buscar...',
  clearTrigger = null,
  readOnly = false,
  dropdownMinWidth = DEFAULT_MIN_WIDTH,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [panelStyle, setPanelStyle] = useState({});
  const containerRef = useRef(null);
  const portalRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  const prevClearTriggerRef = useRef(clearTrigger);
  const grid = gridCols(campos);

  onSelectRef.current = onSelect;

  const updatePanelPosition = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const gutter = 12;
    const vw = document.documentElement.clientWidth || window.innerWidth;
    const vh = document.documentElement.clientHeight || window.innerHeight;
    const maxPanel = Math.max(280, vw - gutter * 2);
    const desired = typeof dropdownMinWidth === 'number'
      ? dropdownMinWidth
      : parseInt(String(dropdownMinWidth), 10) || DEFAULT_MIN_WIDTH;
    const width = Math.min(Math.max(desired, rect.width, 480), maxPanel);

    // Espaço disponível abrindo para a direita (a partir da esquerda do campo)
    // vs. para a esquerda (alinhado à direita do campo)
    const spaceRight = vw - gutter - rect.left;
    const spaceLeft = rect.right - gutter;

    let left;
    if (width <= spaceRight) {
      // Cabe abrindo para a direita — alinha com a esquerda do campo
      left = rect.left;
    } else if (width <= spaceLeft) {
      // Não cabe à direita: abre para a esquerda, alinhado à direita do campo
      left = rect.right - width;
    } else {
      // Não cabe em nenhum lado: usa o lado com mais espaço e limita a largura
      left = spaceLeft >= spaceRight ? rect.right - width : rect.left;
    }

    // Clamp final — nunca sai da viewport
    left = Math.min(Math.max(left, gutter), vw - gutter - width);

    const spaceBelow = vh - rect.bottom - gutter;
    const spaceAbove = rect.top - gutter;
    let top = rect.bottom + 4;
    let maxHeight = Math.min(420, Math.max(160, spaceBelow));
    if (spaceBelow < 180 && spaceAbove > spaceBelow) {
      maxHeight = Math.min(420, Math.max(160, spaceAbove - 4));
      top = Math.max(gutter, rect.top - 4 - maxHeight);
    }

    setPanelStyle({
      top,
      left,
      width,
      maxHeight,
    });
  };

  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    updatePanelPosition();
    const onReposition = () => updatePanelPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [isOpen, filteredOptions.length, dropdownMinWidth]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (defaultValue == null || defaultValue === '') {
      setSelected(null);
      setSearchTerm('');
      return;
    }
    const dv = Number(defaultValue);
    const item = options.find(opt =>
      opt.id === defaultValue ||
      Number(opt.id) === dv ||
      String(opt.id) === String(defaultValue) ||
      String(opt.codigo ?? opt.Codigo ?? '') === String(defaultValue) ||
      String(opt.value ?? '') === String(defaultValue)
    );
    if (item) {
      setSelected(item);
      const firstKey = (campos && campos[0]) ? campos[0].key : 'descricao';
      const labelParts = campos.slice(0, 2).map((c) => String(item[c.key] || '').trim()).filter(Boolean);
      setSearchTerm(labelParts.length > 1 ? labelParts.join(' — ') : String(item[firstKey] || ''));
    } else if (options.length > 0) {
      setSelected(null);
      setSearchTerm('');
    }
  }, [defaultValue, options, campos]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inInput = containerRef.current && containerRef.current.contains(event.target);
      const inPortal = portalRef.current && portalRef.current.contains(event.target);
      if (!inInput && !inPortal) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (clearTrigger !== null && clearTrigger !== prevClearTriggerRef.current) {
      prevClearTriggerRef.current = clearTrigger;
      setSearchTerm('');
      setSelected(null);
      setFilteredOptions([]);
      setIsOpen(false);
      if (onSelectRef.current) onSelectRef.current(null);
    }
  }, [clearTrigger]);

  const handleSearch = (value) => {
    if (readOnly) return;
    setSearchTerm(value);
    setIsOpen(true);

    if (!value.trim()) {
      setFilteredOptions(options || []);
      setSelected(null);
      if (onSelect) onSelect(null);
      return;
    }

    const searchLower = value.toLowerCase();
    const filtered = options.filter(option =>
      campos.some(c => String(option[c.key] || '').toLowerCase().includes(searchLower))
    );
    setFilteredOptions(filtered);
  };

  const abrirPesquisaCompleta = () => {
    if (readOnly) return;
    setIsOpen(true);
    setFilteredOptions(options || []);
  };

  const handleSelect = (item) => {
    setSelected(item);
    const labelParts = campos.slice(0, 2).map((c) => String(item[c.key] || '').trim()).filter(Boolean);
    setSearchTerm(labelParts.length > 1 ? labelParts.join(' — ') : String(item[campos[0].key] || ''));
    setIsOpen(false);
    if (onSelect) onSelect(item);
  };

  const isWrapCol = (key) => {
    const k = (key || '').toLowerCase();
    return k === 'descricao' || k === 'nome' || k === 'texto';
  };

  const dropdown = isOpen
    ? createPortal(
        <Dropdown ref={portalRef} style={panelStyle}>
          {filteredOptions.length > 0 ? (
            <>
              <ColumnHeader $grid={grid}>
                {campos.map(c => (
                  <Column key={c.key} $wrap={false}>{c.label}</Column>
                ))}
              </ColumnHeader>
              {filteredOptions.map((option) => (
                <DropdownItem
                  key={option.id || option.codigo || option.Codigo}
                  onClick={() => handleSelect(option)}
                  className={selected?.id === option.id || selected?.codigo === option.codigo ? 'selected' : ''}
                  $grid={grid}
                >
                  {campos.map(c => (
                    <Column key={c.key} title={String(option[c.key] || '')} $wrap={isWrapCol(c.key)}>
                      {String(option[c.key] || '-')}
                    </Column>
                  ))}
                </DropdownItem>
              ))}
            </>
          ) : (
            <div style={{ padding: '12px', textAlign: 'center', color: '#6c757d' }}>
              {searchTerm.trim() ? 'Nenhum resultado encontrado' : 'Digite para buscar...'}
            </div>
          )}
        </Dropdown>,
        document.body
      )
    : null;

  return (
    <SearchContainer ref={containerRef} $open={isOpen}>
      <Input
        type="text"
        readOnly={readOnly}
        value={searchTerm}
        onChange={(e) => handleSearch(e.target.value)}
        onFocus={() => {
          if (readOnly) return;
          setIsOpen(true);
          if (!searchTerm.trim() && options.length > 0) {
            setFilteredOptions(options);
          }
        }}
        placeholder={placeholder}
        style={readOnly ? { background: '#f4f4f4', cursor: 'not-allowed' } : undefined}
      />
      <LupaButton type="button" onClick={abrirPesquisaCompleta} aria-label="Buscar" disabled={readOnly} style={readOnly ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}>
        <i className="pi pi-search" />
      </LupaButton>
      {dropdown}
    </SearchContainer>
  );
};

export default BuscaDinamica;
